import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Trash2, Image, Video, CheckCircle, XCircle, Clock, Cpu } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

const ENGINE_LABELS = {
  local: 'מודל מקומי',
  gemini: 'Gemini',
  gemini_overflow: 'Gemini (נפילת עומס)',
  none: 'ללא סינון AI'
};

const formatWait = (ms) => {
  if (ms === null || ms === undefined) return null;
  if (ms < 1000) return `${ms} מ"ש`;
  return `${(ms / 1000).toFixed(1)} שניות`;
};

export default function MessagesTab() {
  const [messages, setMessages] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 300,
    total: 0,
    pages: 0
  });
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadMessages();
    loadStats();
  }, [pagination.page]);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/messages?page=${pagination.page}&limit=${pagination.limit}`);
      setMessages(response.data.messages);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('שגיאה בטעינת הודעות');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/messages/stats/summary');
      setStats(response.data);
    } catch (error) {
      console.error('שגיאה בטעינת סטטיסטיקות');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק הודעה זו?')) {
      return;
    }
    
    try {
      await api.delete(`/messages/${id}`);
      toast.success('הודעה נמחקה בהצלחה');
      loadMessages();
      loadStats();
    } catch (error) {
      toast.error('שגיאה במחיקת הודעה');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';

    let normalized = dateString;

    // אם התאריך מגיע מ-SQLite בפורמט "YYYY-MM-DD HH:MM:SS"
    if (!dateString.includes('T')) {
      normalized = dateString.replace(' ', 'T') + 'Z';
    }

    const date = new Date(normalized);

    if (Number.isNaN(date.getTime())) {
      return dateString;
    }

    return date.toLocaleString('he-IL', {
      timeZone: 'Asia/Jerusalem',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && messages.length === 0) {
    return <div className="text-center py-12">טוען הודעות...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header & Stats */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">הודעות</h2>
        
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-blue-600 font-medium">סה"כ הודעות</div>
              <div className="text-2xl font-bold text-blue-900">{stats.total}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-green-600 font-medium">אושרו</div>
              <div className="text-2xl font-bold text-green-900">{stats.approved}</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <div className="text-sm text-red-600 font-medium">נדחו</div>
              <div className="text-2xl font-bold text-red-900">{stats.rejected}</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-sm text-purple-600 font-medium">נשלחו</div>
              <div className="text-2xl font-bold text-purple-900">{stats.sent}</div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="text-sm text-yellow-600 font-medium">עם תמונה</div>
              <div className="text-2xl font-bold text-yellow-900">{stats.withImage}</div>
            </div>
          </div>
        )}
      </div>

      {/* Messages List */}
      <div className="space-y-4">
        {messages.map((msg) => (
          <div key={msg.id} className="bg-white rounded-lg shadow border border-gray-200">
            {/* Header */}
            <div className="flex justify-between items-start p-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${msg.gemini_approved ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <div>
                  <h3 className="font-semibold text-gray-900">{msg.channel_name}</h3>
                  <p className="text-sm text-gray-500">@{msg.channel_username}</p>
                </div>
                {msg.category && (
                  <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                    {msg.category}
                  </span>
                )}
              </div>
              
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">{formatDate(msg.created_at)}</span>
                <button
                  onClick={() => handleDelete(msg.id)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 sm:p-6">
              {/* Right Side - Original */}
              <div className="md:border-l-2 border-blue-200 md:pl-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">📝 הודעה מקורית</h4>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-gray-800 whitespace-pre-wrap text-sm">
                    {msg.original_text || msg.text}
                  </p>
                </div>
              </div>

              {/* Left Side - Processed */}
              <div className="border-r-2 border-green-200 pr-4">
                <h4 className="text-sm font-semibold text-green-900 mb-2">✨ לאחר עיבוד</h4>
                <div className={`rounded-lg p-4 ${msg.gemini_approved ? 'bg-green-50' : 'bg-red-50'}`}>
                  {msg.gemini_approved ? (
                    <>
                      <p className="text-gray-800 whitespace-pre-wrap text-sm mb-3">
                        {msg.filtered_text || msg.text}
                      </p>
                      {msg.filtered_text !== msg.text && msg.filtered_text && (
                        <div className="text-xs text-green-700 bg-green-100 rounded px-2 py-1 inline-block mb-2">
                          ✂️ טקסט נחתך
                        </div>
                      )}
                      {msg.gemini_reason && (
                        <div className="text-xs bg-blue-100 rounded p-2 mt-2">
                          <p className="font-medium text-blue-800">תשובת Gemini:</p>
                          <p className="text-blue-700 whitespace-pre-wrap">{msg.gemini_reason}</p>
                        </div>
                      )}
                      {msg.rejection_reason && (
                        <div className="text-xs bg-yellow-100 rounded p-2 mt-2">
                          <p className="font-medium text-yellow-800">⚠️ לא נשלח לימות:</p>
                          <p className="text-yellow-700 whitespace-pre-wrap">{msg.rejection_reason}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-start gap-2 text-red-700">
                        <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold">נדחה על ידי AI</p>
                          <div className="text-sm mt-1 bg-red-100 rounded p-2">
                            <p className="font-medium text-red-800">תשובת Gemini:</p>
                            <p className="text-red-700 whitespace-pre-wrap">{msg.gemini_reason || 'לא עומד בקריטריונים'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer - Status */}
            <div className="px-4 sm:px-6 pb-4 border-t border-gray-100 pt-4">
              {/* מדיה */}
              <div className="flex items-center gap-4 text-sm mb-3">
                {msg.image_url && (
                  <div className="flex items-center text-blue-600">
                    <Image className="w-4 h-4 ml-1" />
                    <a href={msg.image_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                      תמונה
                    </a>
                  </div>
                )}
                {msg.video_exists && (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center text-orange-600">
                      <Video className="w-4 h-4 ml-1" />
                      וידאו
                    </div>
                    {msg.video_url && (
                      <a 
                        href={msg.video_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200 transition-colors"
                      >
                        צפייה
                      </a>
                    )}
                  </div>
                )}
              </div>
              
              {/* סטטוס שליחה */}
              <div className="flex flex-wrap gap-2">
                {msg.filter_engine && msg.filter_engine !== 'none' && (
                  <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium">
                    <Cpu className="w-3 h-3" />
                    <span>סונן ע"י {ENGINE_LABELS[msg.filter_engine] || msg.filter_engine}</span>
                  </div>
                )}

                {formatWait(msg.filter_ms) && (
                  <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">
                    <Clock className="w-3 h-3" />
                    <span>המתנה: {formatWait(msg.filter_ms)}</span>
                  </div>
                )}

                {msg.sent_to_api ? (
                  <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                    <CheckCircle className="w-3 h-3" />
                    <span>נשלח לכללי</span>
                  </div>
                ) : null}
                
                {msg.sent_to_phone ? (
                  <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                    <CheckCircle className="w-3 h-3" />
                    <span>נשלח לטלפון</span>
                  </div>
                ) : null}
                
                {msg.sent_to_personal ? (
                  <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-medium">
                    <CheckCircle className="w-3 h-3" />
                    <span>נשלח לאישי</span>
                  </div>
                ) : null}
                
                {!msg.sent_to_api && !msg.sent_to_phone && !msg.sent_to_personal && (
                  <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs">
                    <XCircle className="w-3 h-3" />
                    <span>לא נשלח</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-center items-center gap-4 bg-white rounded-lg shadow p-4">
          <button
            onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
            disabled={pagination.page >= pagination.pages}
            className="p-2 rounded-lg bg-blue-600 text-white disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          
          <span className="text-gray-700 font-medium">
            עמוד {pagination.page} מתוך {pagination.pages}
          </span>
          
          <button
            onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
            disabled={pagination.page <= 1}
            className="p-2 rounded-lg bg-blue-600 text-white disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
}
